import { Trans } from "@lingui/react/macro";
import { every, isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import Typography from "@mui/material/Typography";

import LoadSpinner from "@readai/common/components/LoadSpinner";
import Stack, { Row } from "@readai/pulse/components/Stack";

import TimeSlot from "@app/booking/components/TimeSlot";
import useAvailability from "@app/booking/hooks/useAvailability";
import useSchedulingStore from "@app/booking/stores/scheduling";
import * as Events from "@app/booking/tracking/events";

import NoTimesAvailable from "./NoTimesAvailable";
import { TimeSlotHeader, TimeSlotList } from "./styles";

const BestTimes = ({ onSelectTime }: { onSelectTime?: () => void }) => {
  const { bestOptions, isBestOptionsLoading } = useAvailability();
  const [selectedTime, setSelectedTime] = useSchedulingStore((state) => [
    state.selectedTime,
    state.setSelectedDateAndTime,
  ]);

  if (isBestOptionsLoading) {
    return (
      <Stack
        flexGrow={1}
        sx={{ width: "100%", bgcolor: "elevation.surface.default" }}
        variant="center"
      >
        <LoadSpinner />
      </Stack>
    );
  }

  if (isEmpty(bestOptions) || every(isEmpty, Object.values(bestOptions))) {
    return (
      <Stack flexGrow={1} width="100%">
        <NoTimesAvailable />
      </Stack>
    );
  }

  const { thisWeek, nextWeek } = bestOptions;

  const handleSelectTime = (date: DateTime) => {
    Events.selectDate({ date, kind: "best" });
    setSelectedTime(date);
    onSelectTime?.();
  };

  return (
    <Row
      flexWrap="wrap"
      flexGrow={1}
      sx={{ width: "100%", p: 2 }}
      spacing={3}
      justifyContent="center"
      alignItems="flex-start"
    >
      <Stack spacing={1}>
        <TimeSlotHeader>
          <Trans>This week</Trans>
        </TimeSlotHeader>
        <TimeSlotList width="300px">
          {isEmpty(thisWeek) ? (
            <Typography
              variant="bodyPrimaryReg"
              alignSelf="flex-start"
              color="text.weak"
            >
              <Trans>No times available this week</Trans>
            </Typography>
          ) : (
            thisWeek.map((option) => (
              <TimeSlot
                key={option.time.toString()}
                onClick={handleSelectTime}
                time={option.time}
                recommended={option.isRecommended}
                selected={
                  selectedTime ? option.time.equals(selectedTime) : false
                }
                includeDate
              />
            ))
          )}
        </TimeSlotList>
      </Stack>
      <Stack spacing={1}>
        <TimeSlotHeader>
          <Trans>Next week</Trans>
        </TimeSlotHeader>
        <TimeSlotList width="300px">
          {isEmpty(nextWeek) ? (
            <Typography
              variant="bodyPrimaryReg"
              alignSelf="flex-start"
              color="text.weak"
            >
              <Trans>No times available next week</Trans>
            </Typography>
          ) : (
            nextWeek.map((option) => (
              <TimeSlot
                key={option.time.toString()}
                onClick={handleSelectTime}
                time={option.time}
                recommended={option.isRecommended}
                selected={
                  selectedTime ? option.time.equals(selectedTime) : false
                }
                includeDate
              />
            ))
          )}
        </TimeSlotList>
      </Stack>
    </Row>
  );
};

export default BestTimes;
